import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useState, useEffect, useRef } from 'react';
import DynamicIcon from '../../../components/ui/DynamicIcon';
import Quiz from '../Quiz';
import useQuizStore from '../../../store/quizStore';
import api from '../../../services/api';
import { PartyPopper, Play, Trash2 } from 'lucide-react';
import PlayModePicker from '../../../components/quiz/PlayModePicker';
import useFeaturePing, { pingFeature } from '../../../hooks/useFeaturePing';
import tagLabel from '../../../utils/tagLabel';
import Illustration from '../../../components/ui/Illustration';
import illuAutrement from '../../../assets/illustrations/sentrainer-autrement.svg?raw';
import illuMaitrise from '../../../assets/illustrations/ma-maitrise.svg?raw';
import illuRevisions from '../../../assets/illustrations/revisions-du-jour.svg?raw';
import illuQuizIntelligent from '../../../assets/illustrations/quiz-intelligent.svg?raw';
import illuFaiblesses from '../../../assets/illustrations/mes-faiblesses.svg?raw';
import illuConsolidation from '../../../assets/illustrations/consolidation.svg?raw';

const SMART_MODES = [
    {
        id: 'mixed',
        label: 'Quiz intelligent',
        desc: 'Mix équilibré : un peu de faiblesses, de révisions, de consolidation et de nouveautés. Idéal au quotidien.',
        illu: illuQuizIntelligent,
    },
    {
        id: 'weaknesses',
        label: 'Mes faiblesses',
        desc: 'Concentré sur les notions où votre taux de réussite est le plus bas. Pour combler les lacunes.',
        illu: illuFaiblesses,
    },
    {
        id: 'consolidation',
        label: 'Consolidation',
        desc: 'Notions presque maîtrisées (70 à 85 %) — quelques répétitions pour les ancrer durablement.',
        illu: illuConsolidation,
    },
];

const ERROR_MODES = [
    { id: 'never', label: 'Jamais réussies', desc: 'Questions auxquelles vous n\'avez jamais répondu correctement.', color: '#dc3545', group: 'performance' },
    { id: 'sometimes', label: 'Instables', desc: 'Questions parfois réussies, parfois ratées.', color: '#e67e22', group: 'performance' },
    { id: 'recent', label: 'Erreurs récentes', desc: 'Erreurs dans vos 3 dernières tentatives.', color: '#ffc107', group: 'recent' },
];

const COLORS = { good: '#28a745', ok: '#e67e22', bad: '#dc3545' };
const getColor = (pct) => pct >= 75 ? COLORS.good : pct >= 50 ? COLORS.ok : COLORS.bad;

// Une catégorie "a des questions" si au moins un de ses sous-sujets en contient.
// Les catégories vides (ex. "Non classé" sans contenu) sont inutiles comme filtre
// → on les masque des sélecteurs (le backend les garde pour la gestion de contenu).
const catHasQuestions = (c) => (c.sous_sujets || []).some(ss => (ss.nb_questions || 0) > 0);

// Libellés des sessions intelligentes en cours (par smart_mode renvoyé par l'API).
const SMART_SESSION_LABELS = {
    mixed: 'Quiz intelligent',
    weaknesses: 'Mes faiblesses',
    consolidation: 'Consolidation',
    'sm2-due': 'Révisions du jour',
    revision: 'Révision',
};

// Date de démarrage d'une session (MySQL 'YYYY-MM-DD HH:MM:SS') → "26/06/2026 14:30".
function fmtStartedAt(d) {
    if (!d) return null;
    const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d.replace(' ', 'T'));
    return isNaN(dt.getTime()) ? null
        : dt.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Styles partagés de la refonte "synthèse" de l'onglet Progression.
const SECTION_HEAD = { fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '0.95em', color: 'var(--heading)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 };
const FAMILY_HEAD = { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 10 };
const HERO_CTA = { marginTop: 12, padding: '13px 26px', borderRadius: 12, border: 'none', cursor: 'pointer', background: '#fff', color: '#6d28d9', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1em' };

export default function TabEntrainement({
    scopeInfo, parcoursActif = '', categories, user, has_access,
    engineAccess, requireEngine,
    masteryLoading, masteryData,
    smartQuizLoading, setSmartQuizLoading,
    smartQuizActive, setSmartQuizActive,
    onQuizDone,
    errorMode, setErrorMode,
    errorCatId, setErrorCatId,
    errorLoading, errorSearchDone, setErrorSearchDone,
    errorSujets = [], errorPlayable = 0, errorTotal,
    errorQuizActive, setErrorQuizActive,
    errorQuizLoading, setErrorQuizLoading,
    requireAccess, searchErrors,
    quizOverlayBg,
    onGoToQuiz,
    verdictAction,
    verdictUpsell,
}) {
    useFeaturePing('smart_quiz');
    // Replis : les deux coquilles ScopeLanding ne passent pas encore les mêmes
    // props (cf. index.jsx vs ScopeLandingInline) — sans accès moteur explicite,
    // on retombe sur l'ancien comportement basé sur l'abonnement.
    const canEngine = engineAccess ?? has_access;
    const gateEngine = requireEngine ?? requireAccess;
    const { startSmartQuiz, startErrorsQuiz, startSm2DueQuiz, resumeSession } = useQuizStore();

    const isUserScope = scopeInfo?.content_source === 'user';
    // Thematique publiee : la source pilote la TABLE lue par l'API (partner_questions).
    const isPartnerScope = scopeInfo?.content_source === 'partner';
    const [refreshKey, setRefreshKey] = useState(0); // bumpé au retour du guidé → refetch SM-2/sessions
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pendingSmart, setPendingSmart] = useState([]);
    const [pendingErrors, setPendingErrors] = useState([]);
    // SM-2 dues summary : { due_today, due_overdue, due_next_7d, total_tracked }
    const [sm2Summary, setSm2Summary] = useState(null);

    // Pending mode picker popup: { type: 'smart'|'error', smartMode?, title }
    const [modePicker, setModePicker] = useState(null);
    // Confirm dialog for session cancellation
    const errorResultsRef = useRef(null);

    // Scroll to results when search completes
    useEffect(() => {
        if (errorSearchDone && errorResultsRef.current) {
            errorResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [errorSearchDone]);

    // Fetch pending smart + error sessions + SM-2 summary for this scope.
    // Relu au changement de concours : l'intercepteur d'api.js y joint le parcours actif.
    useEffect(() => {
        if (!scopeInfo?.id || !user) return;
        api.get(`/qcm/session.php?scope_id=${scopeInfo.id}&type=smart`)
            .then(({ data }) => setPendingSmart(data.data || []))
            .catch(e => console.warn('Erreur chargement sessions:', e));
        api.get(`/qcm/session.php?scope_id=${scopeInfo.id}&type=errors`)
            .then(({ data }) => setPendingErrors(data.data || []))
            .catch(e => console.warn('Erreur chargement sessions:', e));
        api.get(`/qcm/sm2-due.php`, { params: { scope_id: scopeInfo.id, summary: 1 } })
            .then(({ data }) => setSm2Summary(data.data || null))
            .catch(e => console.warn('Erreur chargement SM-2 summary:', e));
    }, [scopeInfo?.id, user, smartQuizActive, errorQuizActive, refreshKey, parcoursActif]);

    // Click on a smart card → show mode picker (unless resuming a pending session)
    const onSmartCardClick = (smartMode) => {
        if (!gateEngine()) return;
        const SMART_LABELS = { mixed: 'Quiz intelligent', weaknesses: 'Mes faiblesses', consolidation: 'Consolidation' };
        setModePicker({ type: 'smart', smartMode, title: SMART_LABELS[smartMode] || 'Quiz intelligent' });
    };

    // Lancer le quiz d'erreurs (tous sujets, ou un seul sujet) → choix du mode de jeu
    const onErrorLaunchClick = (sujet = null) => {
        if (!gateEngine()) return;
        pingFeature('errors_quiz');
        const ERROR_LABELS = { never: 'Jamais réussies', sometimes: 'Instables', recent: 'Erreurs récentes' };
        const mode = ERROR_LABELS[errorMode] || 'Quiz erreurs';
        setModePicker({ type: 'error', title: sujet ? `${mode} · ${sujet.label}` : mode, sousSujetId: sujet?.sous_sujet_id || 0 });
    };

    // Mode selected in popup → launch the quiz. `count` = nombre choisi (SM-2 only).
    const onModeSelected = async (playMode, count) => {
        const pending = modePicker;
        setModePicker(null);
        if (!pending) return;

        const scopeImage = scopeInfo?.image || null;
        if (pending.type === 'smart') {
            setSmartQuizLoading(true);
            const ok = await startSmartQuiz(scopeInfo.id, pending.smartMode, 15, { isCustom: isUserScope, isPartner: isPartnerScope, playMode, scopeImage });
            setSmartQuizLoading(false);
            if (ok) setSmartQuizActive(true);
        } else if (pending.type === 'sm2') {
            setSmartQuizLoading(true);
            const ok = await startSm2DueQuiz(scopeInfo.id, count || 20, { isCustom: isUserScope, isPartner: isPartnerScope, playMode, scopeImage });
            setSmartQuizLoading(false);
            if (ok) setSmartQuizActive(true);
        } else if (pending.type === 'error') {
            setErrorQuizLoading(true);
            const ok = await startErrorsQuiz(scopeInfo.id, errorMode, 20, {
                isCustom: isUserScope,
                isPartner: isPartnerScope,
                categorieId: errorCatId,
                sousSujetId: pending.sousSujetId || 0,
                playMode,
                scopeImage,
            });
            setErrorQuizLoading(false);
            if (ok) {
                setErrorQuizActive(true);
                setErrorSearchDone(false);
            }
        }
    };

    // Click on the SM-2 "Révisions du jour" card.
    // La séance est plafonnée à 20 (les plus urgentes) ; on indique le vrai retard
    // en petit dans le popup, sans en faire un badge anxiogène sur la carte.
    const onSm2CardClick = () => {
        if (!gateEngine()) return;
        pingFeature('revisions_jour');
        const dueNow = (sm2Summary?.due_today || 0) + (sm2Summary?.due_overdue || 0);
        // Presets de séance (légère → poussée), plafonnés au retard réel. "Tout" si petit reste.
        const LBL = { 10: 'Express', 20: 'Standard', 40: 'Longue' };
        const opts = [10, 20, 40].filter(n => n <= dueNow);
        const top = opts.length ? opts[opts.length - 1] : 0;
        if (top < dueNow && dueNow <= 40) opts.push(dueNow);
        const counts = (opts.length ? opts : [dueNow]).map(n => ({ value: n, label: LBL[n] || (n === dueNow ? 'Tout' : '') }));
        const note = dueNow > 20 ? `${dueNow} questions en attente — on commence par les plus urgentes.` : null;
        // Liste toutes les séances "Révisions du jour" en cours (reprenables), avec leur date de démarrage.
        const resumes = (pendingSmart || [])
            .filter(s => s.smart_mode === 'sm2-due')
            .map(s => ({
                id: s.id,
                label: `${s.nb_answered}/${s.nb_questions}`,
                date: fmtStartedAt(s.created_at),
                onResume: () => { setModePicker(null); handleResumeSmart(s); },
            }));
        setModePicker({
            type: 'sm2',
            title: 'Révisions du jour',
            note,
            resumes,
            counts: counts.length > 1 ? counts : null,
            defaultCount: 20,
        });
    };

    const handleResumeErrorQuiz = async (session) => {
        const ok = await resumeSession(session.id, { scopeId: scopeInfo.id, isCustom: isUserScope, isPartner: isPartnerScope });
        if (ok) setErrorQuizActive(true);
    };

    const handleResumeSmart = async (session) => {
        const ok = await resumeSession(session.id, { scopeId: scopeInfo.id, isCustom: isUserScope, isPartner: isPartnerScope });
        if (ok) setSmartQuizActive(true);
    };

    const closeQuizOverlay = (setter) => {
        useQuizStore.getState().reset();
        setter(false);
        onQuizDone();
    };

    // Sub-tab Intelligent/Erreurs — DÉCLARÉ ICI (avant tout early return) pour
    // respecter les Rules of Hooks : ce hook DOIT être appelé sur tous les rendus.
    const [subTab, setSubTabRaw] = useState(() => {
        try { return localStorage.getItem('monquiz_entrainement_subtab') || 'intelligent'; } catch { return 'intelligent'; }
    });
    const setSubTab = (tab) => { setSubTabRaw(tab); try { localStorage.setItem('monquiz_entrainement_subtab', tab); } catch {} };

    // Annuler une séance (29/10/2026) : seulement sans aucune réponse, et le serveur le revérifie au moment de
    // supprimer (only_if_empty) ; s'il refuse (réponse enregistrée entre-temps), on relit les séances.
    // Une séance avec des réponses ne s'annule pas : on la reprend et « S'arrêter là » enregistre ce qui a été fait.
    const cancelSession = async (session) => {
        try {
            await api.delete('/qcm/session.php', { data: { session_id: session.id, only_if_empty: true } });
            setPendingErrors(prev => prev.filter(s => s.id !== session.id));
            setPendingSmart(prev => prev.filter(s => s.id !== session.id));
        } catch (e) {
            console.warn('Erreur annulation session:', e);
            setRefreshKey(k => k + 1);
        }
    };

    if (!user) return <p className="sl-login-prompt">Connectez-vous pour accéder à la progression intelligente.</p>;

    // Smart quiz or error quiz active — fullscreen overlay (la croix de fermeture
    // est gérée par le Quiz lui-même via .quiz-modal-close--fixed).
    if (smartQuizActive || errorQuizActive) {
        return (
            <div className={`quiz-modal-overlay ${quizOverlayBg?.backgroundImage ? 'has-bg-image' : ''}`} style={quizOverlayBg}>
                <Quiz
                    scopeInfoProp={scopeInfo}
                    verdictAction={verdictAction}
                    verdictUpsell={verdictUpsell}
                    onClose={() => {
                        useQuizStore.getState().reset();
                        if (smartQuizActive) setSmartQuizActive(false);
                        if (errorQuizActive) setErrorQuizActive(false);
                        onQuizDone();
                    }}
                />
            </div>
        );
    }

    if (masteryLoading) return <div className="loading">Analyse de votre profil...</div>;

    const hasData = masteryData && masteryData.summary.total_questions > 0;

    const renderErreurs = () => {
        if (!user) return <p className="sl-login-prompt">Connectez-vous pour retravailler vos erreurs.</p>;

        return (
            <div className="sl-erreurs">
                {/* Pending error sessions — resumable */}
                {pendingErrors.length > 0 && (
                    <div className="sl-error-pending">
                        <h4 className="sl-error-pending-title">Sessions en cours</h4>
                        {pendingErrors.map((s) => {
                            const modeInfo = ERROR_MODES.find(m => m.id === s.error_mode);
                            return (
                                <div key={s.id} className="sl-error-pending-card" style={{ '--pick-color': modeInfo?.color || '#6366f1' }}>
                                    <span className="sl-error-pending-indicator" />
                                    <div className="sl-error-pending-info">
                                        <strong>{modeInfo?.label || s.error_mode}</strong>
                                        <span>{s.nb_answered}/{s.nb_questions} répondu(es){s.created_at ? ` · démarrée le ${fmtStartedAt(s.created_at)}` : ''}</span>
                                    </div>
                                    <button className="btn btn-primary btn-sm" onClick={() => handleResumeErrorQuiz(s)}>
                                        <Play size={16} /> Reprendre
                                    </button>
                                    {s.nb_answered === 0 && (
                                        <button
                                            className="btn btn-xs btn-ghost sl-error-pending-cancel"
                                            title="Annuler cette séance (aucune réponse)"
                                            onClick={() => cancelSession(s)}
                                        >
                                            <Trash2 size={10} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Mode selection — split layout: performance | recent */}
                <div className="sl-error-picker">
                    <div className="sl-error-picker-group">
                        <span className="sl-error-picker-heading">Par performance</span>
                        {ERROR_MODES.filter(m => m.group === 'performance').map((m) => (
                            <button
                                key={m.id}
                                className={`sl-error-pick ${errorMode === m.id ? 'active' : ''}`}
                                onClick={() => { setErrorMode(m.id); setErrorSearchDone(false); }}
                                style={{ '--pick-color': m.color }}
                            >
                                <span className="sl-error-pick-indicator" />
                                <div className="sl-error-pick-text">
                                    <strong>{m.label}</strong>
                                    <span>{m.desc}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                    <div className="sl-error-picker-divider" />
                    <div className="sl-error-picker-group">
                        <span className="sl-error-picker-heading">Par historique</span>
                        {ERROR_MODES.filter(m => m.group === 'recent').map((m) => (
                            <button
                                key={m.id}
                                className={`sl-error-pick ${errorMode === m.id ? 'active' : ''}`}
                                onClick={() => { setErrorMode(m.id); setErrorSearchDone(false); }}
                                style={{ '--pick-color': m.color }}
                            >
                                <span className="sl-error-pick-indicator" />
                                <div className="sl-error-pick-text">
                                    <strong>{m.label}</strong>
                                    <span>{m.desc}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Category filter */}
                {categories.length > 0 && (
                    <div className="sl-error-filter">
                        <label>Categorie :</label>
                        <div className="sl-error-chips">
                            <button
                                className={`sl-chip ${errorCatId === 0 ? 'active' : ''}`}
                                onClick={() => { setErrorCatId(0); setErrorSearchDone(false); }}
                            >
                                Toutes
                            </button>
                            {categories.filter(catHasQuestions).map((c) => (
                                <button
                                    key={c.id}
                                    className={`sl-chip ${c.image ? 'sl-chip--bg' : ''} ${errorCatId === c.id ? 'active' : ''}`}
                                    onClick={() => { setErrorCatId(c.id); setErrorSearchDone(false); }}
                                    style={{
                                        ...(c.image ? { backgroundImage: `url(${c.image})`, '--cat-color': c.couleur_theme || 'var(--primary)' } : {}),
                                        ...(!c.image && errorCatId === c.id && c.couleur_theme ? { background: c.couleur_theme, borderColor: c.couleur_theme } : {}),
                                    }}
                                >
                                    <span>
                                        {c.icon && <DynamicIcon name={c.icon} size={13} />}
                                        {c.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search button */}
                <div className="sl-error-search">
                    <button className="btn btn-primary" onClick={() => searchErrors(1)} disabled={errorLoading}>
                        {errorLoading ? 'Recherche...' : 'Rechercher les erreurs'}
                    </button>
                </div>

                {/* Results */}
                {errorSearchDone && (
                    <div className="sl-error-results" ref={errorResultsRef}>
                        {errorTotal === 0 ? (
                            <div className="sl-empty-state">
                                <span className="sl-empty-icon"><PartyPopper size={40} strokeWidth={1.5} /></span>
                                <p>{masteryData?.summary?.total_questions > 0
                                    ? 'Aucune erreur trouvée pour ce mode. Bravo !'
                                    : 'Commencez par faire quelques quiz pour valider vos connaissances.'
                                }</p>
                            </div>
                        ) : (() => {
                            // Où se concentrent les erreurs : une tuile par sujet, la plus chargée
                            // d'abord ; un clic rejoue ce sujet seul, le bouton principal mélange tout.
                            const maxCount = Math.max(1, ...errorSujets.map(x => x.count));
                            const nbQuiz = Math.min(20, errorPlayable);
                            const horsAcces = errorTotal - errorPlayable;
                            return (
                            <>
                                <div className="sl-err-hero">
                                    <div className="sl-err-hero__text">
                                        <span className="sl-err-hero__kicker">Ta marge de progression</span>
                                        <h3 className="sl-err-hero__title">
                                            <strong>{errorTotal}</strong> erreur{errorTotal > 1 ? 's' : ''} à transformer en points
                                        </h3>
                                        <p className="sl-err-hero__sub">
                                            Sur {errorSujets.length} sujet{errorSujets.length > 1 ? 's' : ''}. Reprends-les tant qu’elles sont fraîches : c’est là que tu progresses le plus vite.
                                            {horsAcces > 0 && <> {horsAcces} ne {horsAcces > 1 ? 'sont' : 'est'} plus rejouable{horsAcces > 1 ? 's' : ''} (accès ou concours).</>}
                                        </p>
                                    </div>
                                    <button className="btn btn-primary sl-err-hero__cta" onClick={() => onErrorLaunchClick()} disabled={errorQuizLoading || nbQuiz === 0}>
                                        <Play size={16} strokeWidth={2.4} />
                                        {errorQuizLoading ? 'Chargement…' : `Lancer un quiz de ${nbQuiz} question${nbQuiz > 1 ? 's' : ''}${errorPlayable > nbQuiz ? ` (sur ${errorPlayable})` : ''}`}
                                    </button>
                                </div>

                                <div className="sl-err-sujets">
                                    {errorSujets.map((sj) => (
                                        <button key={sj.sous_sujet_id} type="button" className="sl-err-sujet"
                                            onClick={() => onErrorLaunchClick(sj)} disabled={errorQuizLoading || !sj.playable}
                                            title={sj.playable ? `Rejouer les ${Math.min(20, sj.playable)} erreurs de ce sujet` : 'Plus accessible avec ton accès actuel'}
                                            style={{ '--err-share': `${Math.round(sj.count / maxCount * 100)}%` }}>
                                            <span className="sl-err-sujet__count">{sj.count}</span>
                                            <span className="sl-err-sujet__body">
                                                <span className="sl-err-sujet__label">{sj.label}</span>
                                                {sj.categorie && <span className="sl-err-sujet__cat">{sj.categorie}</span>}
                                                <span className="sl-err-sujet__bar"><span /></span>
                                            </span>
                                            <span className="sl-err-sujet__go">{sj.playable ? <><span className="sl-err-sujet__go-text">Rejouer</span> <Play size={13} strokeWidth={2.6} aria-label="Rejouer" /></> : 'Hors accès'}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                            );
                        })()}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="sl-entrainement">
            {/* ===== À FAIRE MAINTENANT ===== */}
            {hasData && (
                <div style={{ marginBottom: 24 }}>
                    <div style={SECTION_HEAD}><Illustration className="sl-section-illu" svg={illuRevisions} color={scopeInfo?.couleur_theme} />Révisions espacées</div>
                    {(() => {
                        const dueNow = (sm2Summary?.due_today || 0) + (sm2Summary?.due_overdue || 0);
                        return dueNow > 0 ? (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 18, padding: '24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                                <div>
                                    <strong>{dueNow} question{dueNow > 1 ? 's' : ''} à revoir</strong>
                                    <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>Questions arrivées à échéance selon tes réponses précédentes.</p>
                                </div>
                                <button className="btn btn-primary" onClick={onSm2CardClick} disabled={smartQuizLoading}>Choisir ma séance</button>
                            </div>
                        ) : (
                            <p style={{ color: 'var(--text-muted)' }}>Aucune révision espacée due aujourd’hui. Tu peux utiliser les outils de révision ci-dessous.</p>
                        );
                    })()}

                    {/* Séances en cours : même carte que la « Reprise rapide » de l'onglet Quiz (catégorie en petit,
                        titre, avancement ; en mobile, « Reprendre » sur toute la largeur sous le texte). */}
                    {(pendingSmart.length + pendingErrors.length) > 0 && (
                        <div className="sl-continue-wrap sl-pending-wrap">
                            <h3 className="sl-continue-heading">Sessions en cours</h3>
                            {[...pendingSmart, ...pendingErrors].map((s) => {
                                const isErr = !!s.error_mode;
                                const modeInfo = isErr ? ERROR_MODES.find(m => m.id === s.error_mode) : null;
                                const label = isErr ? (modeInfo?.label || s.error_mode) : (SMART_SESSION_LABELS[s.smart_mode] || 'Quiz intelligent');
                                return (
                                    <div key={s.id} className="sl-error-pending-card sl-continue-card" style={{ '--pick-color': modeInfo?.color || '#7c3aed' }}>
                                        <span className="sl-error-pending-indicator" />
                                        <div className="sl-error-pending-info">
                                            <span className="sl-continue-cat">{isErr ? 'Corriger mes erreurs' : (s.smart_mode === 'sm2-due' ? 'Révisions espacées' : "S'entraîner intelligemment")}</span>
                                            <strong>{label}</strong>
                                            <span>{s.nb_answered}/{s.nb_questions}{s.created_at ? ` · démarrée le ${fmtStartedAt(s.created_at)}` : ''}</span>
                                        </div>
                                        <button className="btn btn-primary btn-sm sl-continue-resume" onClick={() => isErr ? handleResumeErrorQuiz(s) : handleResumeSmart(s)}><Play size={16} /> Reprendre</button>
                                        {s.nb_answered === 0 && (
                                            <button className="btn btn-xs btn-ghost sl-error-pending-cancel" title="Annuler cette séance (aucune réponse)" onClick={() => cancelSession(s)}><Trash2 size={12} /></button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ===== S'ENTRAÎNER AUTREMENT (tiroir : 2 familles) ===== */}
            {hasData && (
                <div style={{ marginBottom: 24 }}>
                    <div style={SECTION_HEAD}><Illustration className="sl-section-illu" svg={illuAutrement} color={scopeInfo?.couleur_theme} />S'entraîner autrement</div>
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 13, overflow: 'hidden' }}>
                        <button type="button" onClick={() => setDrawerOpen(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 22px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', textAlign: 'left' }}>
                            <div>
                                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05em', color: 'var(--heading)' }}>Choisir mon entraînement</div>
                                <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginTop: 2 }}>Quiz intelligent · Mes faiblesses · Consolidation · Cibler mes erreurs</div>
                            </div>
                            <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.92em', flexShrink: 0 }}>{drawerOpen ? 'Replier ▴' : 'Déplier ▾'}</span>
                        </button>
                        {drawerOpen && (
                            // Une colonne : en haut les trois modes qui lancent aussitôt une séance (côte à
                            // côte sur ordinateur), en dessous « Corriger mes erreurs », qui demande des
                            // choix et produit une liste, sur toute la largeur.
                            <div style={{ padding: '2px 22px 22px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                                <div>
                                    <div style={FAMILY_HEAD}>S'entraîner intelligemment</div>
                                    <div className="sl-action-list">
                                        {SMART_MODES.map((m) => (
                                            <div key={m.id} className="sl-action-card">
                                                <button className="sl-action-card__main" onClick={() => onSmartCardClick(m.id)} disabled={smartQuizLoading}>
                                                    <Illustration className="sl-action-card__illu" svg={m.illu} color={scopeInfo?.couleur_theme} />
                                                    <div className="sl-action-card__body"><strong>{m.label}</strong><span className="sl-action-card__desc">{m.desc}</span></div>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div style={FAMILY_HEAD}>Corriger mes erreurs</div>
                                    {renderErreurs()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== MA MAÎTRISE ===== */}
            {hasData && (
                <div>
                    <div style={SECTION_HEAD}><Illustration className="sl-section-illu" svg={illuMaitrise} color={scopeInfo?.couleur_theme} />Ma maîtrise</div>
                    <div className="sl-mastery-summary">
                        <div className="sl-mastery-stats">
                            <div className="sl-mastery-stat">
                                <div className="sl-mastery-value" style={{ color: getColor(masteryData.summary.global_success) }}>
                                    {masteryData.summary.global_success}%
                                </div>
                                <div className="sl-mastery-label">Taux de réussite</div>
                            </div>
                            <div className="sl-mastery-stat">
                                <div className="sl-mastery-value">{masteryData.summary.total_questions}</div>
                                <div className="sl-mastery-label">Questions répondues</div>
                            </div>
                        </div>
                    </div>

                    {masteryData.by_categorie.length > 0 && (
                        <div className="sl-mastery-section">
                            <h3 className="sl-section-title">Maîtrise par catégorie</h3>
                            <div className="sl-mastery-cats">
                                {masteryData.by_categorie.map((c, i) => (
                                    <div key={i} className="sl-mastery-cat">
                                        <span className="sl-mastery-cat-label">{c.categorie}</span>
                                        <div className="sl-mastery-tag-bar">
                                            <div className="sl-mastery-tag-fill" style={{ width: `${c.success_rate}%`, background: getColor(c.success_rate) }} />
                                        </div>
                                        <span className="sl-mastery-tag-pct" style={{ color: getColor(c.success_rate) }}>{c.success_rate}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {masteryData.weakest_tags.length > 0 && (
                        <div className="sl-mastery-section">
                            <h3 className="sl-section-title">Notions les plus faibles</h3>
                            <div className="sl-mastery-tags">
                                {masteryData.weakest_tags.map((t, i) => (
                                    <div key={i} className="sl-mastery-tag">
                                        <div className="sl-mastery-tag-info">
                                            <span className="sl-mastery-tag-name">{tagLabel(t.tag)}</span>
                                            <span className="sl-mastery-tag-detail">{t.attempts} tentatives</span>
                                        </div>
                                        <div className="sl-mastery-tag-bar">
                                            <div className="sl-mastery-tag-fill" style={{ width: `${t.success_rate}%`, background: getColor(t.success_rate) }} />
                                        </div>
                                        <span className="sl-mastery-tag-pct" style={{ color: getColor(t.success_rate) }}>{t.success_rate}%</span>
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-primary btn-sm sl-mastery-action" onClick={() => onSmartCardClick('weaknesses')} disabled={smartQuizLoading}>
                                Travailler mes faiblesses
                            </button>
                        </div>
                    )}

                    {masteryData.stale_tags.length > 0 && (
                        <div className="sl-mastery-section">
                            <h3 className="sl-section-title">A reviser (pas vu depuis longtemps)</h3>
                            <div className="sl-mastery-tags">
                                {masteryData.stale_tags.map((t, i) => (
                                    <div key={i} className="sl-mastery-tag">
                                        <div className="sl-mastery-tag-info">
                                            <span className="sl-mastery-tag-name">{tagLabel(t.tag)}</span>
                                            <span className="sl-mastery-tag-detail">{t.days_since} jours</span>
                                        </div>
                                        <div className="sl-mastery-tag-bar">
                                            <div className="sl-mastery-tag-fill" style={{ width: `${t.success_rate}%`, background: getColor(t.success_rate) }} />
                                        </div>
                                        <span className="sl-mastery-tag-pct" style={{ color: getColor(t.success_rate) }}>{t.success_rate}%</span>
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-primary btn-sm sl-mastery-action" onClick={onSm2CardClick} disabled={smartQuizLoading}>
                                Lancer mes révisions
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!hasData && !masteryLoading && (
                <div className="sl-empty-state">
                    <span className="sl-empty-icon"><ContentIllustration name="brain" size={64} /></span>
                    <p>Commencez par faire quelques quiz pour débloquer la progression intelligente.</p>
                    <button className="btn btn-primary btn-sm" onClick={onGoToQuiz}>
                        Commencer un quiz
                    </button>
                </div>
            )}

            {/* Mode picker popup */}
            {modePicker && (
                <PlayModePicker
                    title={modePicker.title}
                    note={modePicker.note}
                    resumes={modePicker.resumes}
                    counts={modePicker.counts}
                    defaultCount={modePicker.defaultCount}
                    onSelect={onModeSelected}
                    onCancel={() => setModePicker(null)}
                />
            )}

        </div>
    );
}
